import Phaser from "phaser";
import { config } from "@/config";
import { ensureAvatarAtlas, AVATAR_ATLAS_KEY } from "@/world/objects/AvatarAtlas";
import { getZoneConfig, type ZoneTransition } from "@/world/map/ZoneManifest";
import { routeToZone, SIDESCROLL_SCENE_KEY } from "@/scenes/routing";

/**
 * Side-scroll shell.
 *
 * Renders a static side-on scene (sky + hills + ground) with a side-facing
 * character that walks left/right via A/D or arrow keys, mirroring the sprite
 * on direction change. F at the portal returns to the previous zone.
 *
 * Sidescroll-specific avatar handling (Step 6 of the multi-perspective plan):
 * picks the East-facing atlas frame and flips horizontally on movement,
 * collapsing the 8-dir atlas to the 2-dir mirror convention typical of
 * platformers. Iso/top-down/oblique scenes still use the full 8-dir mapping
 * via each Projection's `worldDirToFrame`.
 *
 * Still deferred: gravity, AABB collision, parallax layers, network.
 */
export class SideScrollScene extends Phaser.Scene {
  static readonly KEY = SIDESCROLL_SCENE_KEY;

  /** Walking speed in pixels per millisecond. */
  private static readonly WALK_SPEED = 0.22;
  private static readonly EDGE_MARGIN = 32;

  private fKey?: Phaser.Input.Keyboard.Key;
  private leftKey?: Phaser.Input.Keyboard.Key;
  private rightKey?: Phaser.Input.Keyboard.Key;
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private exitTransition?: ZoneTransition;
  private character?: Phaser.GameObjects.Sprite;

  constructor() {
    super(SideScrollScene.KEY);
  }

  create(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    const horizonY = h * 0.7;

    this.cameras.main.setBackgroundColor("#7a9ad4");
    ensureAvatarAtlas(this);

    // Gradient sky (top to horizon)
    const sky = this.add.graphics();
    sky.fillGradientStyle(0x4a6ab4, 0x4a6ab4, 0xb4d6f0, 0xb4d6f0, 1);
    sky.fillRect(0, 0, w, horizonY);

    // Sun
    this.add.circle(w * 0.78, h * 0.18, 36, 0xfff0a0, 0.95).setStrokeStyle(2, 0xfde68a);

    // Distant hills silhouette
    const hills = this.add.graphics();
    hills.fillStyle(0x4a8a5a, 1);
    hills.beginPath();
    hills.moveTo(0, horizonY - 12);
    hills.lineTo(w * 0.18, horizonY - 70);
    hills.lineTo(w * 0.34, horizonY - 30);
    hills.lineTo(w * 0.52, horizonY - 90);
    hills.lineTo(w * 0.68, horizonY - 40);
    hills.lineTo(w * 0.86, horizonY - 75);
    hills.lineTo(w, horizonY - 30);
    hills.lineTo(w, horizonY);
    hills.lineTo(0, horizonY);
    hills.closePath();
    hills.fillPath();

    // Ground
    this.add.rectangle(0, horizonY, w, h - horizonY, 0x6a4a2a).setOrigin(0, 0);
    const groundLine = this.add.graphics();
    groundLine.lineStyle(3, 0x4a3a1a, 1);
    groundLine.lineBetween(0, horizonY, w, horizonY);

    // Side-facing character. "d0" = East in the 8-dir atlas; flipX handles "W".
    const character = this.add.sprite(w / 2, horizonY, AVATAR_ATLAS_KEY, "d0");
    character.setOrigin(0.5, 1);
    character.setScale(2.5);
    this.character = character;
    this.tweens.add({
      targets: character,
      y: horizonY - 4,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });

    // Resolve the exit transition from the manifest (this zone's first transition).
    let label = "Return";
    try {
      const cfg = getZoneConfig(this, config.zoneId);
      const tr = cfg.transitions[0];
      if (tr) {
        this.exitTransition = tr;
        if (tr.label) label = tr.label;
      }
    } catch {
      // Manifest missing — F still works as a no-op
    }

    // Portal indicator (right side)
    const portalX = w - 100;
    const portalY = horizonY - 40;
    const portal = this.add.circle(portalX, portalY, 28, 0xa080f0, 0.55);
    portal.setStrokeStyle(2, 0xc0a0ff);
    this.tweens.add({
      targets: portal,
      alpha: { from: 0.55, to: 0.2 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
    });

    this.add.text(portalX, portalY - 50, `${label}\n[F] travel`, {
      fontSize: "12px",
      color: "#ffffff",
      backgroundColor: "rgba(0,0,0,0.65)",
      padding: { left: 6, right: 6, top: 3, bottom: 3 },
      align: "center",
    }).setOrigin(0.5, 1);

    // Title banner
    this.add.text(w / 2, 24, "Side-Scroll Stub — 2-dir mirror avatar (Step 6)", {
      fontSize: "15px",
      color: "#ffffff",
      backgroundColor: "rgba(0,0,0,0.55)",
      padding: { left: 12, right: 12, top: 5, bottom: 5 },
    }).setOrigin(0.5, 0);

    this.add.text(w / 2, h - 12, "A/D or ← → to walk · F at portal to return · No gravity yet", {
      fontSize: "10px",
      color: "#cccccc",
    }).setOrigin(0.5, 1);

    const keyboard = this.input.keyboard;
    if (keyboard) {
      this.fKey = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F);
      this.leftKey = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
      this.rightKey = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
      this.cursors = keyboard.createCursorKeys();
    }
  }

  update(_time: number, delta: number): void {
    // Horizontal movement + sprite flip — collapses 8-dir atlas to 2-dir mirror.
    if (this.character) {
      const left = this.leftKey?.isDown || this.cursors?.left?.isDown;
      const right = this.rightKey?.isDown || this.cursors?.right?.isDown;
      let dx = 0;
      if (left) dx -= 1;
      if (right) dx += 1;
      if (dx !== 0) {
        this.character.x += dx * SideScrollScene.WALK_SPEED * delta;
        this.character.x = Phaser.Math.Clamp(
          this.character.x,
          SideScrollScene.EDGE_MARGIN,
          this.scale.width - SideScrollScene.EDGE_MARGIN,
        );
        this.character.setFlipX(dx < 0);
      }
    }

    // Portal activation
    if (!this.fKey || !this.exitTransition) return;
    if (!Phaser.Input.Keyboard.JustDown(this.fKey)) return;
    const tr = this.exitTransition;
    routeToZone(this, tr.targetZone, tr.targetSpawn);
  }
}
